import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  CleaningServices,
  Schedule,
  PlayArrow,
  CheckCircle,
  TrendingUp,
  Group
} from '@mui/icons-material';

import { getHousekeepingDashboard } from '../services/api';
import { useSnackbar } from 'notistack';

export const HKDashboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const response = await getHousekeepingDashboard();
        setStats(response.data?.stats || {});
      } catch (error) {
        enqueueSnackbar('Failed to load dashboard data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [enqueueSnackbar]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading Dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  const summaryCards = [
    { 
      label: 'Total Tasks', 
      value: stats.totalTasks,
      icon: <CleaningServices sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
      progress: 100
    },
    { 
      label: 'Pending', 
      value: stats.pending,
      icon: <Schedule sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
      progress: stats.totalTasks ? (stats.pending / stats.totalTasks) * 100 : 0
    },
    { 
      label: 'In Progress', 
      value: stats.inProgress,
      icon: <PlayArrow sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
      progress: stats.totalTasks ? (stats.inProgress / stats.totalTasks) * 100 : 0
    },
    { 
      label: 'Completed', 
      value: stats.completed,
      icon: <CheckCircle sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
      progress: stats.totalTasks ? (stats.completed / stats.totalTasks) * 100 : 0
    }
  ];

  const cardStyle = {
    borderRadius: 3,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
    }
  };

  const getWorkloadColor = (tasks) => {
    if (tasks >= 15) return 'error';
    if (tasks >= 10) return 'warning';
    return 'success';
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: 'grey.50',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          fontWeight={700}
          gutterBottom
          sx={{ 
            color: 'text.primary',
            background: 'linear-gradient(45deg, #2c3e50, #3498db)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          Housekeeping Dashboard
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp sx={{ fontSize: 20 }} />
          Real-time overview of cleaning operations
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        {summaryCards.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={cardStyle}>
              <CardContent sx={{ p: 3, position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: item.color,
                    opacity: 0.1
                  }}
                >
                  {item.icon}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: `${item.color}15`,
                      borderRadius: 2,
                      p: 1,
                      mr: 2,
                      color: item.color
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5 }}
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{ color: item.color }}
                    >
                      {item.value ?? 0}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={item.progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: `${item.color}20`,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: item.color,
                        borderRadius: 3
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {item.progress.toFixed(1)}% of total
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Tasks by Ward */}
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 3,
                  pb: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CleaningServices />
                  Top Wards (Tasks)
                </Typography>
              </Box>

              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 2 }}>Ward</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>
                      Task Count
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(stats.tasksByWard || []).map((row, index) => (
                    <TableRow
                      key={row.ward}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2, fontWeight: 600 }}>
                        {row.ward}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, fontWeight: 600 }}>
                        {row.count}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        <Chip 
                          label={row.count > 10 ? "High" : "Normal"} 
                          size="small"
                          color={row.count > 10 ? "warning" : "success"}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}

                  {(!stats.tasksByWard || stats.tasksByWard.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          <CleaningServices sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body1">
                            No ward data available
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Staff Workload */}
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 3,
                  pb: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Group />
                  Staff Workload
                </Typography>
              </Box>

              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 2 }}>Staff Member</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 2 }}>
                      Tasks Assigned
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, py: 2 }}>
                      Workload
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(stats.staffWork || []).map((staff, index) => (
                    <TableRow
                      key={staff.staffId}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2, fontWeight: 600 }}>
                        {staff.name}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2, fontWeight: 600 }}>
                        {staff.tasks}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        <Chip 
                          label={staff.tasks >= 15 ? "Heavy" : staff.tasks >= 10 ? "Moderate" : "Light"} 
                          size="small"
                          color={getWorkloadColor(staff.tasks)}
                          variant="filled"
                        />
                      </TableCell>
                    </TableRow>
                  ))}

                  {(!stats.staffWork || stats.staffWork.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          <Group sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                          <Typography variant="body1">
                            No staff data available
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};